<template>
  <div class="word-area">
    <div class="word-section">
      <p class="right-text line-height-text">＊＊第＊＊＊号</p>
      <p class="right-text line-height-text">＊＊＊＊年＊＊月＊＊日</p>
      <p class="left-text">＊＊＊長</p>
      <p class="left-text line-height-text text-indent">＊＊＊＊様</p>
      <p class="right-text department-address">{{ department.department_address }}</p>
      <p class="right-text word-break">九州旅客鉄道株式会社</p>
      <p class="right-text word-break representative">
        {{ department.representative_rank_name }} {{ department.representative_name }}
      </p>
      <p>
        <span class="center-text line-height-text">&nbsp;</span>
      </p>
      <p class="center-text line-height-text">
        <span class="answer-draft-title">
          {{ title }}
        </span>
        <span>（回答）</span>
      </p>
      <p>
        <span class="center-text line-height-text">&nbsp;</span>
      </p>
      <p class="left-text line-height-text">時下、ますますご清祥のこととお慶び申し上げます。</p>
      <p class="left-text line-height-text">弊社業務に関しましては、平素より格別のご配慮を賜り厚くお礼申しあげます。</p>
      <p class="left-text line-height-text text-indent">
        さて、{{ applicantTime }}付でお申し出のありました標題につきましては、下記の条件により施行願います。
      </p>
      <p class="left-text line-height-text">なお、本回答ご了承のうえは本書全文記載の請書を提出して下さい。</p>
      <p class="center-text line-height-text">記</p>
      <p>
        <span class="center-text line-height-text">&nbsp;</span>
      </p>
      <p>
        <span class="left-text line-height-text"
          ><ol>
            <li v-for="preview in answerPreviews" :key="preview.answer_draft_item_id">
              {{ preview.text }}
            </li>
          </ol></span
        >
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, watch, reactive, PropType, computed } from 'vue'
import { Department } from '@/api'
import { ListRequest } from '@/requests/department'
import { AxiosResponse } from 'axios'
import { formatDateJP } from '@/services/date'
import { AnswerPreviewCreate } from '@/requests/answerPreview'
import { AnswerPreviewRequest, AnswerPreviewResponse } from '@/api'

interface AnswerPreview {
  answer_draft_item_id: number | undefined
  text: string | undefined
}

export default defineComponent({
  name: 'AnswerPreview',
  emits: ['update:modelValue'],
  props: {
    items: {
      type: Array as PropType<number[]>,
      required: true,
    },
    proximity: {
      type: Object,
      required: true,
    },
    //回答相手略称
    answerApplicantAbbreviation: {
      type: String,
      required: true,
    },
    //回答案タイトル
    answerDraftTitle: {
      type: String,
      required: true,
    },
  },

  async setup(props) {
    // wordプレビューの申請日時用に申請日時を[yyyy年mm月dd月]形式に変換
    const applicantTime = formatDateJP(props.proximity.applicant_time)
    const answerPreviews = reactive<AnswerPreview[]>([])
    const title = computed(() => {
      if (props.answerDraftTitle != '') {
        return props.answerDraftTitle
      } else {
        return props.proximity.applicant_title
      }
    })

    /**
     * プレビューの項目に出力する回答案リスト生成
     */
    watch(
      () => props.items,
      async () => {
        if (props.items.length === 0) {
          answerPreviews.splice(0)
          return
        }

        const requestData: AnswerPreviewRequest = {
          answer_draft_title: props.answerDraftTitle,
          answer_applicant_abbreviation: props.answerApplicantAbbreviation,
          answer_draft_items: props.items,
          request_header_id: props.proximity.request_header_id,
        }

        const request = new AnswerPreviewCreate(requestData)
        await request.exec()
        if (request.success) {
          const { data } = request.getResponse() as AxiosResponse<AnswerPreviewResponse>
          //初期化
          answerPreviews.splice(0)
          Object.assign(answerPreviews, data)
        }
      },
      { deep: true }
    )

    /**
     * 対象の工事詳細の保守区マスタの取得
     */
    const department = reactive<Department>({
      department_id: 0,
      department_code: '',
      department_name: '',
      department_short_name: '',
      is_institution: false,
      is_electricity: false,
      is_land: false,
      department_address: '',
      discussion_location: '',
      discussion_tel: '',
      representative_rank_name: '',
      representative_name: '',
      department_sort: 0,
      department_mail_name: '',
    })

    // 工事詳細の保守区マスタリストを取得
    const request = new ListRequest()
    await request.exec()
    if (request.success) {
      const { data } = request.getResponse() as AxiosResponse<Department[]>
      // 取得した保守区マスタリストから対象の工事詳細の保守区マスタ情報を取得
      const targetDepartment = data.find((department) => department.department_code === props.proximity.department_code)
      // 取得した保守区マスタ情報をdepartmentに入れる
      Object.assign(department, targetDepartment)
    }

    return {
      answerPreviews,
      department,
      applicantTime,
      title,
    }
  },
})
</script>

<style lang="scss" scoped>
.word-area {
  border: 1px solid #ccc;
  padding: 15px;
}

.word-section {
  -ms-layout-grid: 15.8pt 0pt;
  padding: 15px;
}

.right-text {
  font-family: 'ＭＳ ゴシック';
  letter-spacing: 2.85pt;
  text-align: right;
}

.left-text {
  font-family: 'ＭＳ ゴシック';
  letter-spacing: 2.85pt;
  text-align: left;
}

.center-text {
  font-family: 'ＭＳ ゴシック';
  letter-spacing: 2.85pt;
  text-align: center;
}

.line-height-text {
  line-height: 115%;
}

.word-break {
  word-break: break-all;
}

.text-indent {
  text-indent: 10.5pt;
}
</style>
